import React, { Component, ErrorInfo, ReactNode } from 'react';
import { SomethingWentWrong } from './Components/ErrorHandling/SomethingWentWrong';
import { isStaleChunkError, reloadOnceForStaleChunk } from '../Util/staleChunkReload';
import { reportBoundaryError } from '../Util/_HumanWritten/Observability/observability';

interface Props {
	children?: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
	errorInfo: ErrorInfo | null;
	// A deploy replaced a lazy-loaded chunk under this open tab; we're reloading to recover, so
	// render blank instead of flashing the error page before the reload lands.
	reloadingForUpdate: boolean;
}

class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null,
			reloadingForUpdate: false,
		};
	}

	public static getDerivedStateFromError(error: Error): State {
		// Update state so the next render will show the fallback UI (or blank, while we reload for a
		// stale chunk — the reload itself is triggered from componentDidCatch).
		return {
			hasError: true,
			error: error,
			errorInfo: null,
			reloadingForUpdate: isStaleChunkError(error),
		};
	}

	public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		if (isStaleChunkError(error)) {
			// A lazy-loaded chunk 404'd because a deploy replaced the hashed bundle under this
			// still-open tab. Reload once to pick up the fresh index.html + chunk names.
			if (reloadOnceForStaleChunk()) {
				return; // reload in flight; keep rendering blank
			}
			// Already reloaded recently and it still failed — fall through to the error UI.
			this.setState({ reloadingForUpdate: false });
		}

		console.error('Uncaught error:', error, errorInfo);
		// Render-path errors never reach window.onerror, so push them to Faro/GlitchTip explicitly
		reportBoundaryError(error, errorInfo);
		this.setState({ hasError: true, error: error, errorInfo: errorInfo });
	}

	public render() {
		const { hasError, error, errorInfo, reloadingForUpdate } = this.state;
		const { children } = this.props;

		if (hasError) {
			if (reloadingForUpdate) {
				return null;
			}
			return (
				<>
					<SomethingWentWrong error={error} errorInfo={errorInfo} />
				</>
			);
		}

		return children;
	}
}

export default ErrorBoundary;
